import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { ButtonType, AutoResetButton } from '@tableau/widgets';
import * as styles from './styles/DetailedErrorStyles';
import { DialogProps, DialogWidget } from '@tableau/dialog-api-contract';
import { Messages } from '../gen-src/Localize';

import { MenuItem, MenuEntry, Configuration } from '@tableau/menu-api-contract';
import * as MenuContract from '@tableau/menu-api-contract';
import { MouseEvent } from 'react-dom/node_modules/@types/react';
import { TextLinkKind, TextLinkWidget, Tooltip, TooltipPosition } from '@tableau/widgets';

/**
 * Main UI component for the Detailed Error Dialog
 */

export interface DetailedErrorComponentProps {
  errorDialogTitle: string;
  errorShortMessage: string;
  errorDetails: string[];
  errorCode: string;
  errorHelpLink: string;
  queryDetails: string;
  issueHelperLinks: {[key: string]: string };
  copyTextValue: string;
  onClose: () => void;
  copyText: (text: string) => void;
}

const copyKey = 'copy';
const copyLinkKey = 'copylink';

export const DialogTestId = 'detailedErrorDialog';
export const ErrorShortMessageTestId = 'error-short-message';
export const ErrorDetailsContainerTestId = 'error-details-container';

export class DetailedErrorComponent extends React.Component<DetailedErrorComponentProps, {}> {
  private readonly autoResetBtnRef: React.RefObject<AutoResetButton> = React.createRef();

  public constructor(props: DetailedErrorComponentProps) {
    super(props);
    this.onContextMenu = this.onContextMenu.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
  }

  private onMouseDown(event: MouseEvent<HTMLElement>): void {
    if (event.button === 2) {
      // workaround for desktop to make right clicking not immediately unselect text before the context menu opens
      event.preventDefault();
    }
  }

  private onContextMenu(event: MouseEvent<HTMLElement>): void {
    let element = event.target as HTMLElement;
    let bounds = element.getBoundingClientRect();

    let config: Configuration = {
      position: {
        anchorElement: element,
        anchorOffset: { x: event.clientX - bounds.left, y: event.clientY - bounds.top }
      }
    };
    let menuItems: MenuItem[] = [];

    if (document.getSelection() !== null) {
      menuItems.push(
        {disabled: false, displayValue: Messages.Copy(), key: copyKey, type: 'entry' }
      );
    }

    if (element instanceof HTMLAnchorElement) {
      menuItems.push(
        {disabled: false, displayValue: Messages.CopyLinkAddress(), key: copyLinkKey, type: 'entry' }
      );
    }

    if (menuItems.length > 0) {
      MenuContract.Instance.Show(config, menuItems)
        .then((selected: MenuEntry) => {
          let windowSelection = window.getSelection();
          if (selected.key === copyKey && windowSelection) {
            this.props.copyText(windowSelection.toString());
          } else if (selected.key === copyLinkKey) {
            this.props.copyText((element as HTMLAnchorElement).href.toString());
          }
        }).catch(() => { return; });
    }
  }

  public componentDidMount(): void {
    // Claim focus:
    // We must do this in case focus is currently held by a focus trap. Calling
    // focus() alone does not work as the focus trap will intercept and reclaim
    // focus. Calling blur() first causes the focus trap to give focus back to the
    // body, which it tolerates.
    if (document.activeElement !== null) {
      (document.activeElement as HTMLElement).blur();
    }
    // Find the native browser DOM element (AutoResetButton) and focus on DOMNode if not null
    let domElement = (ReactDOM.findDOMNode(this.autoResetBtnRef.current!) as HTMLElement);
    if (domElement) {
      domElement.focus();
    }
  }

  public shouldRenderErrorDetailsContainer(): boolean {
    if (this.props.errorDetails.length > 0 || this.props.queryDetails) {
      return true;
    }
    return false;
  }

  public renderErrorCode(): JSX.Element | null {
    if (this.props.errorCode) {
      return (
        <div key='error-code' className={'error-code'} style={styles.errorShortMessageStyle}>
          {this.props.errorCode}
        </div>
      );
    }
    return null;
  }

  public render(): JSX.Element {
    const dialogClassName = 'tab-' + DialogTestId;

    let dialogProps: DialogProps = {
      testId: DialogTestId,
      isModal: true,
      showCloseButton: true,
      showTitleBar: true,
      title: this.props.errorDialogTitle,
      dialogWidth: 500,
      onCloseRequest: this.props.onClose
    };

    let errorDetailsContainer = undefined;
    if (this.shouldRenderErrorDetailsContainer()) {
      const errorDetails = this.props.errorDetails.map((error, index) =>
        <div key={'errorDetail-' + index} style={styles.errorDetailsStyle}>
          {error}
        </div>
      );
      const defectInvestigationLinks = Object.keys(this.props.issueHelperLinks).map((key: string, index) =>
        <div key={'issueHelperLinks-' + index} style={styles.defectInvestigationLink}>
          <Tooltip
            followCursor={false}
            getContent={(): JSX.Element => <span>{this.props.issueHelperLinks[key]}</span>}
            position={TooltipPosition.Top}
            testId={'issue-helper-link-tooltip' + index}
          >
            <TextLinkWidget
              href={this.props.issueHelperLinks[key]}
              kind={TextLinkKind.Standard}
              testId={'issue-helper-link' + index}
              target={'_blank'}>
              {key}
            </TextLinkWidget>
          </Tooltip>
        </div>
      );

      errorDetailsContainer =
      <div data-tb-test-id={ErrorDetailsContainerTestId} className={'error-details'} style={styles.errorDetailsContainer}>
        {errorDetails}
        {defectInvestigationLinks}
        <div style={styles.queryDetailsContainer}>{this.props.queryDetails}</div>
      </div>;
    }

    let confirmButton = undefined;
    let dismissButton = undefined;

    return <DialogWidget { ...dialogProps } >
      <div className={dialogProps.testId} onContextMenu={this.onContextMenu} onMouseDown={this.onMouseDown}>
        <div data-tb-test-id={ErrorShortMessageTestId} className={'error-short-message'} role={'alert'}
            style={styles.errorShortMessageStyle}>
          {this.props.errorShortMessage}
        </div>
        {this.renderErrorCode()}

        {errorDetailsContainer}
        <textarea readOnly={true} tabIndex={-1} aria-hidden='true'
          id='copy_text_area' style={styles.copyTextareaStyle} defaultValue={this.props.copyTextValue}></textarea>
        <div style={styles.actionAreaStyle}>
          <div>
            <TextLinkWidget
              href={this.props.errorHelpLink}
              kind={TextLinkKind.Standard}
              testId={'error-support-link'}
              target={'_blank'}>
              {Messages.GoToSupportLinkText()}
            </TextLinkWidget>
          </div>
          <div style={styles.btnGroupStyle}>
            <AutoResetButton testId='copy' label={Messages.CopyErrorMessageLinkText()} buttonType={ButtonType.Outline}
              style={styles.copyTextBtnStyle} handleClick={() => this.props.copyText(this.props.copyTextValue)}
              delayLabel={ Messages.ErrorCopiedText()} ref={this.autoResetBtnRef} handleReset={(): void => {return; } } />
            {dismissButton}
            {confirmButton}
          </div>
        </div>
      </div>
    </DialogWidget>;
  }
}
